/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";

/**
 * HOC for the partner form
 */
class PartnerUrlTypeForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            partnerUrlTypeId: null,
            partnerUrlTypeName: '',
            description: '',
            isDisplayLoader: true,
            errors: {},

            token: '',
            fields: [
                {
                    inputType: 'text',
                    labelName: 'Name *',
                    inputName: 'partnerUrlTypeName',
                    placeholder: 'Please enter partner url type name here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'text',
                    labelName: 'Description *',
                    inputName: 'description',
                    placeholder: 'Please enter description here',
                    required: true,
                    disabled: false
                }
            ]
        };

        // get the parameter from URL
        const partnerUrlTypeId = this.props.match.params.id;

        // set icons to the header
        if (partnerUrlTypeId === undefined) {
            EventEmitter.emit('setPageHeaderData', {
                page: 'partnerUrlTypes',
                pageName: "Partner Url Types",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/partnerUrlTypes",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'partnerUrlTypes',
                pageName: "Partner Url Types",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/partnerUrlTypes",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        // set event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deletePartnerUrlType);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    componentDidMount = async () => {
        // get parameter from URL
        const partnerUrlTypeId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create institution
        if (partnerUrlTypeId !== undefined) {
            // set a token to unsubscribe the async call
            const cancelToken = axios.CancelToken;
            const src = cancelToken.source();

            const url = '/partner-url-types?search=%7BpartnerUrlTypeId:' + partnerUrlTypeId + '%7D';

            await API.get(url, {headers: {Authorization: token}, cancelToken: src.token})
                .then(res => {
                    const data = res.data[0];

                    oldState.partnerUrlTypeId = (data.partnerUrlTypeId !== undefined) ? data.partnerUrlTypeId : null;
                    oldState.partnerUrlTypeName = (data.partnerUrlTypeName !== undefined) ? data.partnerUrlTypeName : null;
                    oldState.description = (data.description !== undefined) ? data.description : null;
                    oldState.token = token;
                    oldState.isDisplayLoader = false;

                    this.setState(oldState);
                }).catch(er => {
                    oldState.isDisplayLoader = false;
                    this.setState(oldState);
                    this.displayErrorsForAPICalls(er);
                });

            src.cancel();
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        // remove all event emitters when unmount the component
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deletePartnerUrlType);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
    };

    /**
     * update the state when user inout a value
     * @param event
     */
    handleChange = event => {
        const key = event.currentTarget.name;
        const val = event.currentTarget.value;

        this.setState({
            [key]: val
        }, () => {this.validate(key, val)});
    };

    /**
     * handle the form submit
     */
    handleSubmit = () => {
        // data to be sent
        let data = null;

        // check its a update or create
        if (this.state.partnerUrlTypeId !== null) {
            data = {
                partnerUrlTypeId: this.state.partnerUrlTypeId,
                partnerUrlTypeName: this.state.partnerUrlTypeName,
                description: this.state.description
            };
        } else {
            data = {
                partnerUrlTypeName: this.state.partnerUrlTypeName,
                description: this.state.description
            };
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            // check update or create
            if (this.state.partnerUrlTypeId !== null) {
                this.updatePartnerUrlType(data);
            } else {
                this.createPartnerUrlType(data);
            }
        }
    };

    /**
     * validate the user inputs
     * @param key - the input name
     * @param value - the input value
     */
    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'partnerUrlTypeName':
                if (value === '')
                    error[key] = "Partner Url Type Name is Required!";
                else if (value === ' ')
                    error[key] = "Partner Url Type Name can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "Partner Url Type Name can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'description':
                if (value === '')
                    error[key] = "Description is Required!";
                else if (value === ' ')
                    error[key] = "Description can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "Partner Url Description can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    /**
     * create partnerUrlType
     * @param data - the data for create new partnerUrlType
     */
    createPartnerUrlType = async (data) => {
        // set a token to unsubscribe the async call
        const cancelToken = axios.CancelToken;
        const src = cancelToken.source();

        await API.post('/partner-url-types',
            {
                "partnerUrlTypeName": data.partnerUrlTypeName,
                "description": data.description
            }, {headers: {Authorization: this.state.token}, cancelToken: src.token}
        ).then(res => {
            this.setAlert("Success", "Success", "Successfully created the partner url types");
            this.props.history.push('/home/partnerUrlTypes');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });

        src.cancel();
    };

    /**
     * update partner
     * @param data - updated partner data
     */
    updatePartnerUrlType = async (data) => {
        // set a token to unsubscribe the async call
        const cancelToken = axios.CancelToken;
        const src = cancelToken.source();

        await API.put('/partner-url-types',
            {
                "partnerUrlTypeId": data.partnerUrlTypeId,
                "partnerUrlTypeName": data.partnerUrlTypeName,
                "description": data.description
            },{headers: {Authorization: this.state.token}, cancelToken: src.token}
        ).then(response => {
            this.setAlert("Success", "Success", "Successfully updated the partner url type");
            this.props.history.push('/home/partnerUrlTypes');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });

        src.cancel();
    };

    /**
     * display the warning message by emitting an event emitter
     */
    displayDeleteWarningMessage = () => {
        if (this.state.partnerUrlTypeId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'partnerUrlTypes',
                name: this.state.partnerUrlTypeName
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the partner url types",
                alertTitle: "Error"
            });
        }

    };

    /**
     * delete partner
     */
    deletePartnerUrlType = async (data) => {
        if (data && (this.props.match.path === "/home/partnerUrlTypes/:id")) {
            // set a token to unsubscribe the async call
            const cancelToken = axios.CancelToken;
            const src = cancelToken.source();

            await API.delete('/partner-url-types?partnerUrlTypeId=' + this.state.partnerUrlTypeId, {headers: {Authorization: this.state.token}, cancelToken: src.token})
                .then(res => {
                    const msg = "Successfully Deleted the Partner Url Type";
                    this.setAlert("Success", "Success", msg);
                    this.props.history.push('/home/partnerUrlTypes');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });

            src.cancel();
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "Partner Url Type is already exist!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    /**
     * fire alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * clear the form
     */
    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.partnerUrlTypeId = null;
        newState.partnerUrlTypeName = "";
        newState.description = "";
        newState.errors = {};

        this.setState(newState);
    };

    /**
     * navigate to previous page
     */
    navigateToPreviousPage = () => {
        this.props.history.push('/home/partnerUrlTypes');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default PartnerUrlTypeForm;
